import React, {
	type SyntheticEvent,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';

import Fuse from 'fuse.js';
import debounce from 'lodash/debounce';

import SearchIcon from '@atlaskit/icon/core/search';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { Show, UNSAFE_useMediaQuery } from '@atlaskit/primitives/responsive';
import Textfield from '@atlaskit/textfield';

import { getObjectIcons, iconObjectImportPrefix, sortIntoCategories } from '../helpers';
import { type AllIconsList, type IconData, type IconsList } from '../types';

import GridSection from './grid-section';
import LegacyIconDetails from './legacy-icon-details';

const fuseOptions: Fuse.IFuseOptions<IconData> = {
	keys: [
		{
			name: 'componentName',
			weight: 1,
		},
		{
			name: 'packageName',
			weight: 1,
		},
		{
			name: 'iconName',
			weight: 1,
		},
		{
			name: 'keywords',
			weight: 1,
		},
	],
	useExtendedSearch: true,
	threshold: 0.4,
	ignoreLocation: true,
};

const LegacyIconDetailsBoxStyles = xcss({
	position: 'sticky',
	insetBlockStart: '64px',
});

const modalIconDetailsStyles = xcss({
	width: '100%',
	borderRadius: 'radius.small',
	overflow: 'auto',
});

type IconExplorerProps = {
	testId?: string;
};

const SearchIconBefore = () => (
	<Box paddingInlineStart="space.100">
		<SearchIcon label="Search" />
	</Box>
);

export function LegacyIconExplorer({ testId }: IconExplorerProps): React.JSX.Element | null {
	const [selectedIcon, setSelectedIcon] = useState<IconData | undefined>();
	const [icons, setIcons] = useState<AllIconsList>();
	const [allIcons, setAllIcons] = useState<AllIconsList>();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const isBelowSm = UNSAFE_useMediaQuery('below.sm');

	const fuseIndex = useRef<Fuse<IconData> | null>(null);

	const search = useCallback((query: string) => {
		if (fuseIndex.current) {
			const results = fuseIndex.current.search<IconData>(query).map((result) => result.item);
			setIcons(sortIntoCategories(results));
		}
	}, []);

	const debouncedSearch = useMemo(() => debounce(search, 300), [search]);
	const handleSearch = useCallback(
		(query: string, opts: { isDebounced?: boolean } = { isDebounced: false }) => {
			if (query !== '') {
				opts.isDebounced ? debouncedSearch(query) : search(query);
			} else {
				setIcons(allIcons);
			}
		},
		[debouncedSearch, allIcons, search],
	);

	useEffect(() => {
		getObjectIcons().then((icons) => {
			const typedIcons = icons as IconsList;
			fuseIndex.current = new Fuse(Object.values(typedIcons), fuseOptions);
			setAllIcons(sortIntoCategories(Object.values(typedIcons)));
			setIcons(sortIntoCategories(Object.values(typedIcons)));
		});
	}, []);

	const closeModal = () => {
		setIsModalOpen(false);
		setSelectedIcon(undefined);
	};

	const updateSelectedIcon = useCallback(
		(icon: IconData) => {
			setSelectedIcon((oldIcon) => (oldIcon?.packageName !== icon.packageName ? icon : undefined));
			isBelowSm?.matches && setIsModalOpen(true);
		},
		[setSelectedIcon, isBelowSm?.matches],
	);

	if (!icons) {
		return null;
	}

	return (
		<Inline space="space.200" testId={testId}>
			<Stack space="space.400" grow="fill">
				<Textfield
					placeholder="Search"
					elemBeforeInput={<SearchIconBefore />}
					onChange={(evt: SyntheticEvent<HTMLInputElement>) =>
						handleSearch(evt.currentTarget.value, {
							isDebounced: true,
						})
					}
				/>
				<GridSection
					isLegacy={true}
					importPath={`${iconObjectImportPrefix}/.../16`}
					heading="Small"
					icons={icons?.['16']}
					onIconClick={updateSelectedIcon}
					selectedIcon={selectedIcon?.packageName}
				/>

				<GridSection
					isLegacy={true}
					importPath={`${iconObjectImportPrefix}/.../24`}
					heading="Medium"
					icons={icons?.['24']}
					onIconClick={updateSelectedIcon}
					selectedIcon={selectedIcon?.packageName}
				/>
			</Stack>
			<ModalTransition>
				{isModalOpen && selectedIcon && (
					// Close button is contained within the LegacyIconDetails
					// eslint-disable-next-line @atlaskit/design-system/use-modal-dialog-close-button
					<Modal onClose={closeModal}>
						<Box xcss={modalIconDetailsStyles}>
							<LegacyIconDetails icon={selectedIcon} isModal onClose={closeModal} />
						</Box>
					</Modal>
				)}
			</ModalTransition>
			<Show above="sm" xcss={LegacyIconDetailsBoxStyles}>
				<LegacyIconDetails icon={selectedIcon} />
			</Show>
		</Inline>
	);
}
